import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, User, Menu, X } from "lucide-react";
import { useState } from "react";
import { LogoMark } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navClass = ({ isActive }) =>
    `text-sm transition-colors ${isActive ? "text-brand-maroon" : "text-brand-ink hover:text-brand-maroon"}`;

  return (
    <header className="sticky top-0 z-40 glass-nav">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <LogoMark size="text-2xl" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={navClass} data-testid="nav-home">Home</NavLink>
          {user && <NavLink to="/search" className={navClass} data-testid="nav-search">Search</NavLink>}
          {user && <NavLink to="/dashboard" className={navClass} data-testid="nav-dashboard">Dashboard</NavLink>}
          {user && <NavLink to="/messages" className={navClass} data-testid="nav-messages">Messages</NavLink>}
          <NavLink to="/plans" className={navClass} data-testid="nav-plans">Membership</NavLink>
          {user?.role === "admin" && <NavLink to="/admin" className={navClass} data-testid="nav-admin">Admin</NavLink>}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <NotificationBell />
              <Link to="/profile" className="flex items-center gap-2 text-sm text-brand-ink hover:text-brand-maroon" data-testid="navbar-user">
                <User className="h-4 w-4" strokeWidth={1.5} />
                <span>{user.name?.split(" ")[0] || "Profile"}</span>
              </Link>
              <button onClick={handleLogout} className="text-sm flex items-center gap-1 text-brand-inkLight hover:text-brand-maroon" data-testid="navbar-logout">
                <LogOut className="h-4 w-4" strokeWidth={1.5} /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-brand-ink hover:text-brand-maroon" data-testid="navbar-login">Sign in</Link>
              <Link to="/register" className="btn-primary text-sm py-2" data-testid="navbar-register">Register Free</Link>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="navbar-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-brand-line bg-white px-6 py-4 space-y-3">
          <Link to="/" onClick={() => setOpen(false)} className="block text-sm">Home</Link>
          {user && <Link to="/search" onClick={() => setOpen(false)} className="block text-sm">Search</Link>}
          {user && <Link to="/dashboard" onClick={() => setOpen(false)} className="block text-sm">Dashboard</Link>}
          {user && <Link to="/messages" onClick={() => setOpen(false)} className="block text-sm">Messages</Link>}
          <Link to="/plans" onClick={() => setOpen(false)} className="block text-sm">Membership</Link>
          {user?.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block text-sm">Admin</Link>}
          {user ? (
            <button onClick={handleLogout} className="block text-sm text-brand-maroon">Logout</button>
          ) : (
            <div className="flex gap-3">
              <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost text-sm py-2">Sign in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="btn-primary text-sm py-2">Register</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
